import React, { useState } from "react";
import AsyncApiComponent from "@asyncapi/react-component/browser/index";
import "@asyncapi/react-component/styles/default.min.css";
import ReactCodeMirror from "@uiw/react-codemirror";
import { yaml } from "@codemirror/lang-yaml";
import { Link } from "react-router";
import { Container, Header, Icon, List, Segment } from "semantic-ui-react";

function WebSocketApis() {
  const [showExampleSnippet, setShowExampleSnippet] = useState(true);
  const [showExampleUi, setShowExampleUi] = useState(true);

  const api = `asyncapi: '2.6.0'
info:
  title: Chat WebSocket API
  version: '1.0.0'
servers:
  chatServer:
    url: wss://chat.example.com/ws
    protocol: wss
channels:
  chat/message:
    publish:
      summary: Client sends a chat message
      message:
        name: ChatMessage
        payload:
          type: object
          properties:
            user:
              type: string
            message:
              type: string
    subscribe:
      summary: Client receives chat messages
      message:
        name: ChatMessage
        payload:
          type: object
          properties:
            user:
              type: string
            message:
              type: string
`;

  return (
    <Container fluid>
      <Header as="h2" textAlign="center">
        WebSocket APIs
      </Header>
      <Header dividing>Introduction</Header>
      The <Link to="/websocket-apis">WebSocket APIs</Link> page will have the
      comprehensive information about the Async API documentation of all the
      WebSocket APIs in the AWS API Gateway. The APIs are categorized based on
      the modules to which the APIs belong. The list of modules can be seen in
      the left sidebar.
      <br />
      <br />
      If you click on the the desired module's name in the sidebar, the list of
      Microservices of the module will be expanded down and then the list of
      routes will be listed down. This is slightly different from the REST APIs
      sidebar. When you finally select the version, the Async API documentation
      of the API will be visualized in the right side of the page.
      <br />
      <br />
      You can test the WebSocket Route by clicking on the Try out button. It
      will redirect you to the WebSocket test client page and enter the URL
      automatically in the URL field. You can also download the API
      documentation file by clicking on the Download API button and selecting
      the file format from the dropdown.
      <Header as="h3">Async APIs</Header>
      An AsyncAPI document for WebSocket APIs is a formal specification that
      describes how event-driven communication occurs over WebSocket
      connections. Unlike traditional REST APIs, WebSocket APIs enable
      bi-directional, real-time messaging between clients and servers. AsyncAPI
      provides a machine-readable and human-friendly way to define:
      <br />
      <br />
      AsyncAPI provides a machine-readable and human-friendly way to define:
      <List.Item as="li">WebSocket endpoints</List.Item>
      <List.Item as="li">Available messages (events)</List.Item>
      <List.Item as="li">
        Communication patterns (who sends or receives what)
      </List.Item>
      <List.Item as="li">Payload structures (schemas)</List.Item>
      <List.Item as="li">Security requirements</List.Item>
      This makes it easier to build, document, and test real-time applications
      such as chat apps, collaborative tools, or live data dashboards.
      <Header as="h4">Core components</Header>
      <List as="ol">
        <List.Item as="li">
          <b>Protocol:</b> Defined as ws or wss in the servers section.
        </List.Item>
        <List.Item as="li">
          <b>Channels:</b> Represent message routes or actions (e.g., chat/join,
          chat/message).
        </List.Item>
        <List.Item as="li">
          <b>Operation:</b> subscribe (listen to messages) and publish (send
          messages).
        </List.Item>
        <List.Item as="li">
          <b>Messages:</b> Payloads sent over the WebSocket, including their
          format (JSON, etc.).
        </List.Item>
        <List.Item as="li">
          <b>Components:</b> Reusable message schemas, security definitions,
          etc.
        </List.Item>
      </List>
      <Header as="h4">Benefits of Using AsyncAPI for WebSocket</Header>
      <List.Item as="li">
        Standardized documentation for front-end and back-end teams
      </List.Item>
      <List.Item as="li">
        Clear schema validation for sent/received messages
      </List.Item>
      <List.Item as="li">
        Code and test generation for faster development
      </List.Item>
      <List.Item as="li">
        Easy integration into CI/CD and DevOps pipelines
      </List.Item>
      <Header dividing>Example</Header>
      <Segment.Group>
        <Segment>
          <Header as="h4" onClick={() => setShowExampleSnippet((s) => !s)}>
            Snippet(yaml){" "}
            <Icon name={`${showExampleSnippet ? "angle up" : "angle down"}`} />
          </Header>
          {showExampleSnippet && (
            <ReactCodeMirror
              editable={false}
              value={api}
              extensions={[yaml()]}
            />
          )}
        </Segment>
        <Segment>
          <Header as="h4" onClick={() => setShowExampleUi((s) => !s)}>
            Async API UI{" "}
            <Icon name={`${showExampleUi ? "angle up" : "angle down"}`} />
          </Header>
          {showExampleUi && <AsyncApiComponent schema={api} />}
        </Segment>
      </Segment.Group>
    </Container>
  );
}

export default WebSocketApis;
